import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import logo from '../assets/appLogoFinalLight.svg';
import {
  DocumentIcon,
  DocumentTextIcon,
  PhotoIcon,
  VideoCameraIcon,
  ArchiveBoxIcon,
  TableCellsIcon,
  MapIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  CheckCircleIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';

// =============================================================================
// FILE TYPE HELPERS
// =============================================================================

const FILE_TYPE_CONFIG = {
  raw_profile_data: { icon: DocumentIcon, label: 'Profile Data', color: 'text-blue-500', bg: 'bg-blue-50' },
  gps_data: { icon: MapIcon, label: 'GPS Data', color: 'text-green-500', bg: 'bg-green-50' },
  analysis_reports: { icon: DocumentTextIcon, label: 'Analysis Report', color: 'text-red-500', bg: 'bg-red-50' },
  proval_project: { icon: DocumentIcon, label: 'ProVAL Project', color: 'text-purple-500', bg: 'bg-purple-50' },
  smoothness_calculations: { icon: TableCellsIcon, label: 'Spreadsheet', color: 'text-green-600', bg: 'bg-green-50' },
  qc_certification: { icon: DocumentTextIcon, label: 'QC Certification', color: 'text-yellow-600', bg: 'bg-yellow-50' },
  agency_forms: { icon: DocumentTextIcon, label: 'Agency Form', color: 'text-gray-600', bg: 'bg-gray-100' },
  image: { icon: PhotoIcon, label: 'Image', color: 'text-pink-500', bg: 'bg-pink-50' },
  roadway_video: { icon: VideoCameraIcon, label: 'Video', color: 'text-orange-500', bg: 'bg-orange-50' },
  corrective_work: { icon: DocumentIcon, label: 'Corrective Work', color: 'text-indigo-500', bg: 'bg-indigo-50' },
  compressed_archives: { icon: ArchiveBoxIcon, label: 'Archive', color: 'text-amber-600', bg: 'bg-amber-50' },
  supplemental_data: { icon: DocumentIcon, label: 'Supplemental Data', color: 'text-gray-500', bg: 'bg-gray-100' },
};

const getFileTypeConfig = (fileType) => {
  return FILE_TYPE_CONFIG[fileType] || { icon: DocumentIcon, label: 'File', color: 'text-gray-500', bg: 'bg-gray-100' };
};

const formatFileSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const isPreviewable = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf'].includes(ext);
};

const isImage = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
};

const getFileExtension = (filename) => {
  return filename?.split('.').pop()?.toUpperCase() || 'FILE';
};

// =============================================================================
// SHORT ID GENERATOR (for creating share links)
// =============================================================================

export const generateShortId = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// =============================================================================
// LEGACY TOKEN SUPPORT (for backwards compatibility)
// =============================================================================

export const encodeFileShareToken = (fileInfo) => {
  const payload = {
    p: fileInfo.storage_path,
    n: fileInfo.filename,
    s: fileInfo.file_size,
    t: fileInfo.file_type,
    e: fileInfo.expires_at,
    u: fileInfo.signed_url,
  };
  return btoa(JSON.stringify(payload));
};

export const decodeFileShareToken = (token) => {
  try {
    const payload = JSON.parse(atob(token));
    return {
      storage_path: payload.p,
      filename: payload.n,
      file_size: payload.s,
      file_type: payload.t,
      expires_at: payload.e,
      signed_url: payload.u,
    };
  } catch {
    return null;
  }
};

// =============================================================================
// HEADER COMPONENT
// =============================================================================

const Header = () => (
  <header className="bg-white border-b border-gray-200">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-14 sm:h-16">
        <div className="flex items-center">
          <img src={logo} alt="Ames Connect" className="h-6 sm:h-8" />
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 text-sm">
          <span className="text-gray-500 hidden sm:inline">Interested in Ames Connect?</span>
          <a
            href="https://www.amesengineering.com/sales/salesRequest"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 font-medium"
          >
            <span className="hidden sm:inline">Connect with our team</span>
            <span className="sm:hidden">Contact sales</span>
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </div>
  </header>
);

// =============================================================================
// FOOTER COMPONENT
// =============================================================================

const Footer = () => (
  <footer className="bg-gray-100 border-t border-gray-200">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <img src={logo} alt="Ames Connect" className="h-5 opacity-60" />
          <span>&copy; {new Date().getFullYear()} Ames Engineering</span>
        </div>
        <div className="flex items-center gap-6 text-sm text-gray-500">
          <a href="https://amesengineering.com" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">
            Website
          </a>
          <a href="https://amesengineering.com/contact" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">
            Contact
          </a>
        </div>
      </div>
    </div>
  </footer>
);

// =============================================================================
// ERROR PAGE COMPONENT
// =============================================================================

const ErrorPage = ({ icon: Icon, iconColor, title, description, details }) => (
  <div className="min-h-screen flex flex-col bg-gray-50">
    <Header />
    <main className="flex-1 flex items-center justify-center p-4 pt-20">
      <div className="text-center max-w-md">
        <div className={`w-20 h-20 ${iconColor === 'red' ? 'bg-red-100' : 'bg-amber-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
          <Icon className={`h-10 w-10 ${iconColor === 'red' ? 'text-red-600' : 'text-amber-600'}`} />
        </div>
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">{title}</h1>
        <p className="text-gray-600 mb-4">{description}</p>
        {details && <p className="text-sm text-gray-400">{details}</p>}
      </div>
    </main>
    <Footer />
  </div>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function FileDownloadPage() {
  const { token } = useParams();
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Determine if this is a short ID (8 chars) or legacy token
  const isShortId = token && token.length <= 12;

  // Load file info - either from database (short ID) or decode from token (legacy)
  useEffect(() => {
    const loadFileInfo = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      if (isShortId) {
        // Short ID - look up from database
        try {
          const { data, error: fetchError } = await supabase
            .from('file_shares')
            .select('*')
            .eq('short_id', token)
            .single();

          if (fetchError || !data) {
            setFileInfo(null);
          } else {
            setFileInfo({
              filename: data.filename,
              file_size: data.file_size,
              file_type: data.file_type,
              signed_url: data.signed_url,
              expires_at: new Date(data.expires_at).getTime(),
            });
          }
        } catch (err) {
          console.error('Error loading file share:', err);
          setFileInfo(null);
        }
      } else {
        // Legacy token - decode from base64
        const decoded = decodeFileShareToken(token);
        setFileInfo(decoded);
      }
      setLoading(false);
    };

    loadFileInfo();
  }, [token, isShortId]);

  const isExpired = useMemo(() => {
    if (!fileInfo?.expires_at) return false;
    return Date.now() > fileInfo.expires_at;
  }, [fileInfo]);

  const expiresIn = useMemo(() => {
    if (!fileInfo?.expires_at) return null;
    const diff = fileInfo.expires_at - Date.now();
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days} day${days > 1 ? 's' : ''}`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    const minutes = Math.floor(diff / (1000 * 60));
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  }, [fileInfo]);

  const expiresDate = useMemo(() => {
    if (!fileInfo?.expires_at) return null;
    return new Date(fileInfo.expires_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }, [fileInfo]);

  useEffect(() => {
    if (!fileInfo || isExpired || !isPreviewable(fileInfo.filename)) {
      setPreviewLoading(false);
      return;
    }

    // Use the pre-generated signed URL from the token
    if (fileInfo.signed_url) {
      setPreviewUrl(fileInfo.signed_url);
      setPreviewLoading(false);
    }
  }, [fileInfo, isExpired]);

  const handleDownload = async () => {
    if (!fileInfo || isExpired || !fileInfo.signed_url) return;

    setDownloading(true);
    setError(null);

    try {
      // Use the pre-generated signed URL from the token
      const a = document.createElement('a');
      a.href = fileInfo.signed_url;
      a.download = fileInfo.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setDownloaded(true);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Download failed. The file may have been moved or deleted.');
    } finally {
      setDownloading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-1 flex items-center justify-center pt-16">
          <div className="flex flex-col items-center gap-3">
            <svg className="animate-spin h-8 w-8 text-gray-400" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="text-gray-500 text-sm">Loading...</span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Invalid token
  if (!fileInfo) {
    return (
      <ErrorPage
        icon={XCircleIcon}
        iconColor="red"
        title="Invalid Link"
        description="This download link is invalid or malformed. Please check the URL and try again."
      />
    );
  }

  // Expired
  if (isExpired) {
    return (
      <ErrorPage
        icon={ClockIcon}
        iconColor="amber"
        title="Link Expired"
        description="This download link has expired and is no longer available."
        details={`Expired on ${expiresDate}`}
      />
    );
  }

  const typeConfig = getFileTypeConfig(fileInfo.file_type);
  const TypeIcon = typeConfig.icon;
  const canPreview = isPreviewable(fileInfo.filename);

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />

      <main className="flex-1 pt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* File Container */}
          <div className="bg-white border border-gray-300 overflow-hidden">
            {/* Preview Area */}
            {canPreview && (
              <div className="bg-gray-900 flex items-center justify-center border-b border-gray-300" style={{ minHeight: '400px', maxHeight: '500px' }}>
                {previewLoading ? (
                  <div className="flex flex-col items-center gap-3">
                    <svg className="animate-spin h-8 w-8 text-gray-400" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="text-gray-400 text-sm font-medium">Loading preview...</span>
                  </div>
                ) : previewUrl ? (
                  isImage(fileInfo.filename) ? (
                    <img
                      src={previewUrl}
                      alt={fileInfo.filename}
                      className="max-h-[500px] max-w-full object-contain"
                    />
                  ) : (
                    <iframe
                      src={previewUrl}
                      title={fileInfo.filename}
                      className="w-full h-[500px]"
                    />
                  )
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <TypeIcon className="h-16 w-16 text-gray-500" />
                    <span className="text-gray-400 text-sm font-medium">Preview unavailable</span>
                  </div>
                )}
              </div>
            )}

            {/* File Info Section */}
            <div className="p-6 sm:p-8 bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                {/* File Icon (show for non-previewable files) */}
                {!canPreview && (
                  <div className="flex-shrink-0 w-20 h-20 bg-gray-200 border border-gray-300 flex flex-col items-center justify-center">
                    <TypeIcon className="h-8 w-8 text-gray-600" />
                    <span className="text-xs font-bold text-gray-600 mt-1 tracking-wide">
                      {getFileExtension(fileInfo.filename)}
                    </span>
                  </div>
                )}

                {/* File Details */}
                <div className="flex-1 min-w-0">
                  <h1 className="text-lg sm:text-xl font-semibold text-gray-900 break-words tracking-tight">
                    {fileInfo.filename}
                  </h1>
                  <p className="mt-1 text-sm text-gray-600 font-medium">
                    {formatFileSize(fileInfo.file_size)}
                  </p>

                  {/* Expiration Notice */}
                  {expiresIn && (
                    <div className="mt-3 inline-flex items-center gap-2 text-sm text-amber-800 bg-amber-100 border border-amber-300 px-3 py-1.5">
                      <ClockIcon className="h-4 w-4 flex-shrink-0" />
                      <span>Expires in <strong>{expiresIn}</strong></span>
                    </div>
                  )}

                  {/* Error Message */}
                  {error && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-red-800 bg-red-100 border border-red-300 px-3 py-1.5">
                      <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  {/* Download Success */}
                  {downloaded && !downloading && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-green-800 bg-green-100 border border-green-300 px-3 py-1.5">
                      <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
                      <span>Download started</span>
                    </div>
                  )}
                </div>

                {/* Download Button */}
                <div className="flex-shrink-0">
                  <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-gray-900 text-white font-medium rounded-sm hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {downloading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span>Preparing...</span>
                      </>
                    ) : (
                      <>
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        <span>Download File</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Having trouble? Contact the person who shared this file with you.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
